import { AI_CHAT_SUCCESS_MESSAGES, AI_CHAT_UI } from "@/constants/aiChat";
import { layoutNodes } from "@/app/(v2)/workflows/_hooks/useLayout";
import type { WorkflowEdgeData } from "@/app/(v2)/workflows/_components/WorkflowEdge";
import {
  buildWorkflowGraphLookups,
  collectAllLoopBodyDescendantIds,
} from "@/app/(v2)/workflows/_lib/workflowGraphAnalysis";
import type { ActionTriggerDefinition } from "@/hooks/useActionTriggerDefinitions";
import type { X402Endpoint } from "@/hooks/useX402Endpoints";
import { buildX402ResultSchema } from "@/hooks/useX402Endpoints";
import { getWorkflowVerticalGap, readStoredCompactViewPreference } from "@/lib/workflow/layoutPolicy";
import { getEditorInputSchemaFromDefinition } from "@/lib/workflow/inputSchema";
import type {
  NodePosition,
  WorkflowDefinition,
  WorkflowInput,
  WorkflowNodeConnector,
  WorkflowUIMetadata,
} from "@/stores/workflowsStore";
import type { AIWorkflow, AIWorkflowDefinition } from "@/types/aiChat";
import type { Edge, Node, ReactFlowInstance } from "@xyflow/react";
import { toast } from "@/lib/toast";

// Re-export from sub-files
export * from "./workflowTransformStream";
export * from "./workflowTransformLayout";
export * from "./workflowTransformRefinement";

// Constants for progressive node layout (exported for sub-files)
export const STREAMING_X_CENTER = 400;

/** Minimal shape for nodes that may carry a branch field (if/wait children). */
interface BranchableNodeData {
  branch?: string;
}

/**
 * Determine the source handle for an IF node's child edge based on branch property.
 *
 * @param childNodeData - The child node data object
 * @returns The source handle string ("source-then" or "source-else") or undefined
 */
export function getIfNodeSourceHandle(childNodeData: BranchableNodeData | undefined): string | undefined {
  const childBranch = childNodeData?.branch;
  if (childBranch === "then") {
    return "source-then";
  } else if (childBranch === "else") {
    return "source-else";
  }
  return undefined;
}

/**
 * Check whether a wait node has branched children (resumed/timed_out).
 * Used to tag nodes with `hasWaitBranches: true` so LogicNode renders side handles.
 */
export function hasWaitBranchChildren(
  nodeData: { type?: string; children?: string[] },
  allNodes: Record<string, { branch?: string }>,
): boolean {
  if (nodeData?.type !== "wait" || !nodeData.children?.length) {
    return false;
  }
  return nodeData.children.some((childKey: string) => {
    const childData = allNodes[childKey];
    return childData?.branch === "resumed" || childData?.branch === "timed_out";
  });
}

/**
 * Determine the source handle for a WAIT node's child edge based on branch property.
 *
 * @param childNodeData - The child node data object
 * @returns The source handle string ("source-resumed" or "source-timed_out") or undefined
 */
export function getWaitNodeSourceHandle(childNodeData: BranchableNodeData | undefined): string | undefined {
  const childBranch = childNodeData?.branch;
  if (childBranch === "resumed") {
    return "source-resumed";
  } else if (childBranch === "timed_out") {
    return "source-timed_out";
  }
  return undefined;
}

/**
 * Determine the source handle for a branching node's child edge.
 * Works for both IF nodes (then/else) and WAIT nodes (resumed/timed_out).
 *
 * @param parentType - The parent node's action type (e.g., "if", "wait")
 * @param childNodeData - The child node data object
 * @returns The source handle string or undefined
 */
export function getBranchingSourceHandle(
  parentType: string,
  childNodeData: BranchableNodeData | undefined,
): string | undefined {
  if (parentType === "if") {
    return getIfNodeSourceHandle(childNodeData);
  } else if (parentType === "wait") {
    return getWaitNodeSourceHandle(childNodeData);
  }
  return undefined;
}

/**
 * Transform API workflow definition to React Flow nodes and edges.
 * This function converts the backend workflow format to the format used by React Flow.
 *
 * @param definition - The workflow definition containing nodes
 * @param workflowType - Optional workflow type
 * @param definitions - Optional action/trigger definitions for enrichment
 * @param placeholderCallbacks - Optional callbacks for placeholder nodes
 * @param _uiMetadata - Optional UI metadata containing saved node positions
 */
export function transformApiToReactFlow(
  definition: WorkflowDefinition | AIWorkflowDefinition,
  workflowType?: string,
  definitions?: ActionTriggerDefinition[],
  placeholderCallbacks?: {
    onDropAction?: (placeholderId: string, dragData: any) => void;
    onShowActionModal?: (placeholderId: string) => void;
    onShowLogicModal?: (placeholderId: string) => void;
  },
  _uiMetadata?: WorkflowUIMetadata,
): { nodes: Node[]; edges: Edge[] } {
  void _uiMetadata;
  let nodes: Node[] = [];
  const edges: Edge[] = [];
  const apiNodes = definition.nodes;

  // Helper to determine node type for React Flow (trigger vs action vs logic)
  // The root node is always the trigger
  // Logic nodes are identified by their category in the definitions
  const getNodeType = (nodeKey: string, nodeData: any): string => {
    if (nodeKey === "root") return "trigger";

    // Block nodes have their own canvas component
    if (nodeData.type === "block") return "block";

    // Check if this is a logic action by looking up in definitions
    if (definitions) {
      const definition = definitions.find(def => def.type === nodeData.type);
      if (definition?.category === "logic") {
        return "logic";
      }
    }

    return "action";
  };

  // Spacing constants for placeholder positioning
  const ySpacing = 200; // Vertical spacing between levels

  // Create placeholder ID counter
  let placeholderCounter = 0;

  // First pass: Create React Flow nodes (without placeholders)
  for (const [nodeKey, nodeData] of Object.entries(apiNodes)) {
    // Temporary position — layoutNodes will recalculate after all nodes are created
    const position = { x: 400, y: 0 };
    const nodeType = getNodeType(nodeKey, nodeData);

    // Get node metadata (name, description, platform) - we'll need to look this up from definitions
    // Preserve node-specific description from API if it exists (e.g., AI-generated descriptions)
    const reactFlowNode: Node = {
      id: nodeKey,
      type: nodeType,
      position,
      draggable: true,
      data: {
        type: nodeData.type,
        name: nodeData.type, // Will be enriched later if needed
        description: ("description" in nodeData && nodeData.description) || "", // Preserve AI-generated node description
        titleOverride: ("titleOverride" in nodeData && nodeData.titleOverride) || "", // Preserve AI-generated title override
        platform: "",
        payload: nodeData.payload || {},
        connector: nodeData.connector, // Preserve connector data from API
        apiNodeId: nodeKey !== "root" ? nodeKey : undefined, // Store the API node ID (except for root)
      },
    };

    nodes.push(reactFlowNode);

    // Create edges for children (node-to-node only)
    // For for-each nodes: API `children` = post-loop nodes → edges get isOnComplete flag
    // For all other nodes: API `children` = regular child edges
    const isForEachNode = nodeType === "logic" && nodeData.type === "for-each";

    if (nodeData.children && Array.isArray(nodeData.children) && nodeData.children.length > 0) {
      // Check if this is a branching node (if-then-else or wait with timeout branches)
      const isBranchingNode = nodeData.type === "if" || nodeData.type === "wait";

      for (const childKey of nodeData.children) {
        const childNodeData = apiNodes[childKey];
        const sourceHandle = isBranchingNode ? getBranchingSourceHandle(nodeData.type, childNodeData) : undefined;
        const isTargetForEach = childNodeData?.type === "for-each";

        // Build edge data: for for-each source nodes, children are post-loop (isOnComplete)
        const edgeData: Record<string, boolean> = {};
        if (isTargetForEach) edgeData.isTargetForEach = true;
        if (isForEachNode) edgeData.isOnComplete = true;

        edges.push({
          id: `${nodeKey}=>${childKey}`,
          source: nodeKey,
          target: childKey,
          type: "workflow",
          ...(sourceHandle && { sourceHandle }),
          ...(Object.keys(edgeData).length > 0 && { data: edgeData }),
        });
      }
    }

    // Create edges for loopBody nodes (loop body for for-each nodes)
    if (nodeData.loopBody && Array.isArray(nodeData.loopBody) && nodeData.loopBody.length > 0) {
      for (const lbKey of nodeData.loopBody) {
        const childNodeData = apiNodes[lbKey];
        const isTargetForEach = childNodeData?.type === "for-each";

        edges.push({
          id: `${nodeKey}=>${lbKey}`,
          source: nodeKey,
          target: lbKey,
          type: "workflow",
          ...(isTargetForEach && { data: { isTargetForEach: true } }),
        });
      }
    }
  }

  // Tag all descendants of for-each nodes so the UI can propagate
  // skipped status (for-each descendants don't receive node_skipped
  // events from the backend). Uses BFS to reach grandchildren etc.
  // IMPORTANT: Stop at nested for-each boundaries — a nested for-each
  // handles tagging its own LoopBody descendants. We still traverse
  // the nested for-each's Children (post-loop) since those live at
  // the same level as the parent for-each. This ensures each node
  // points to its CLOSEST ancestor for-each, so the skipped
  // propagation in useNodeExecutionStatus works correctly.
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  for (const [nodeKey, nodeData] of Object.entries(apiNodes)) {
    if (nodeData.type === "for-each" && (nodeData.children?.length || nodeData.loopBody?.length)) {
      const queue = [...(nodeData.loopBody || []), ...(nodeData.children || [])];
      while (queue.length > 0) {
        const key = queue.shift()!;
        const childNode = nodeMap.get(key);
        if (childNode) {
          childNode.data = { ...childNode.data, parentForEachApiNodeId: nodeKey };
        }
        const childApi = apiNodes[key];
        if (childApi?.type === "for-each") {
          // Nested for-each: only traverse its Children (post-loop).
          // Its LoopBody will be tagged when we process that for-each
          // in the outer loop, pointing to the correct (inner) parent.
          if (childApi.children?.length) {
            queue.push(...childApi.children);
          }
        } else {
          if (childApi?.children?.length) {
            queue.push(...childApi.children);
          }
          if (childApi?.loopBody?.length) {
            queue.push(...childApi.loopBody);
          }
        }
      }
    }
  }

  // Tag wait nodes that have branched children (resumed/timed_out) so the
  // LogicNode component can render side handles instead of a single bottom handle.
  for (const [nodeKey, nodeData] of Object.entries(apiNodes)) {
    if (hasWaitBranchChildren(nodeData, apiNodes)) {
      const waitNode = nodeMap.get(nodeKey);
      if (waitNode) {
        waitNode.data = { ...waitNode.data, hasWaitBranches: true };
      }
    }
  }

  // Always run layoutNodes for consistent tree layout.
  // Saved positions may use stale spacing (e.g., before ONCOMPLETE_GAP_VERTICAL
  // was increased), so we re-layout every time to guarantee correct gaps.
  nodes = layoutNodes(nodes, edges, {
    verticalGap: getWorkflowVerticalGap(readStoredCompactViewPreference()),
  });

  const { childrenMap, onCompleteTargetIds, forEachNodes } = buildWorkflowGraphLookups(nodes, edges);
  const loopBodyDescendantIds = collectAllLoopBodyDescendantIds(forEachNodes, childrenMap, onCompleteTargetIds, {
    excludePlaceholders: true,
  });

  // Second pass: Add placeholder nodes for leaf nodes (after layout is calculated)
  const nodesWithPlaceholders: Node[] = [...nodes];
  for (const [nodeKey, nodeData] of Object.entries(apiNodes)) {
    const hasChildren = nodeData.children && Array.isArray(nodeData.children) && nodeData.children.length > 0;
    const hasLoopBody =
      nodeData.loopBody && Array.isArray(nodeData.loopBody) && (nodeData.loopBody as string[]).length > 0;
    const isLoopBodyDescendant = loopBodyDescendantIds.has(nodeKey);
    if (!hasChildren && !hasLoopBody && !isLoopBodyDescendant) {
      const parentNode = nodes.find(n => n.id === nodeKey);
      if (parentNode) {
        const placeholderId = `placeholder-${nodeKey}-${placeholderCounter++}`;
        const placeholderNode: Node = {
          id: placeholderId,
          type: "placeholder",
          position: { x: parentNode.position.x, y: parentNode.position.y + ySpacing },
          data: {
            // Include placeholder callbacks if provided
            ...(placeholderCallbacks?.onDropAction && { onDropAction: placeholderCallbacks.onDropAction }),
            ...(placeholderCallbacks?.onShowActionModal && {
              onShowActionModal: placeholderCallbacks.onShowActionModal,
            }),
            ...(placeholderCallbacks?.onShowLogicModal && { onShowLogicModal: placeholderCallbacks.onShowLogicModal }),
          },
          draggable: false,
        };
        nodesWithPlaceholders.push(placeholderNode);

        // Add edge to placeholder
        edges.push({
          id: `${nodeKey}=>${placeholderId}`,
          source: nodeKey,
          target: placeholderId,
          type: "smoothstep",
          style: {
            stroke: "#3b82f6",
            strokeWidth: 2,
            strokeDasharray: "5,5",
          },
        });
      }
    }
  }

  return { nodes: nodesWithPlaceholders, edges };
}

/**
 * Transform an AI Workflow object to React Flow format.
 * This is a convenience wrapper around transformApiToReactFlow.
 */
export function transformAIWorkflowToReactFlow(
  workflow: AIWorkflow,
  definitions?: ActionTriggerDefinition[],
  placeholderCallbacks?: {
    onDropAction?: (placeholderId: string, dragData: any) => void;
    onShowActionModal?: (placeholderId: string) => void;
    onShowLogicModal?: (placeholderId: string) => void;
  },
): { nodes: Node[]; edges: Edge[] } {
  return transformApiToReactFlow(
    workflow.definition,
    workflow.type,
    definitions,
    placeholderCallbacks,
    workflow.uiMetadata,
  );
}

/**
 * Minimal x402 endpoint fields needed for node enrichment.
 * Uses Pick to avoid duplicating the full X402Endpoint interface.
 * Includes outputSchema and exampleOutput for building resultSchema (needed for variable picker).
 */
type X402EndpointForEnrichment = Pick<
  X402Endpoint,
  | "id"
  | "name"
  | "description"
  | "baseUrl"
  | "price"
  | "network"
  | "tokenSymbol"
  | "tokenDecimals"
  | "outputSchema"
  | "exampleOutput"
>;

/**
 * Enrich transformed nodes with metadata from action/trigger definitions.
 * This adds name, description, and platform information to nodes.
 *
 * For x402 nodes, also enriches payloads with endpoint metadata by looking up
 * the endpoint by ID or name. This ensures AI-generated x402 nodes display
 * correctly with price/network badges.
 *
 * @param nodes - React Flow nodes to enrich
 * @param definitions - Action/trigger definitions for metadata
 * @param x402Endpoints - Optional x402 endpoints for payload enrichment
 */
export function enrichNodesWithDefinitions(
  nodes: Node[],
  definitions: ActionTriggerDefinition[],
  x402Endpoints?: X402EndpointForEnrichment[],
): Node[] {
  return nodes.map(node => {
    if (node.type === "placeholder") return node;

    // Block nodes are enriched at render time via useBlock() hook — skip definition lookup
    if (node.type === "block") return node;

    const definition = definitions.find(def => def.type === node.data.type);
    let enrichedData = { ...node.data };

    // Add definition metadata
    if (definition) {
      enrichedData = {
        ...enrichedData,
        name: definition.name,
        // Preserve node-specific description (e.g., AI-generated).
        // Do NOT fall back to definition.description — that's the integration
        // catalog description and would overwrite AI descriptions on autosave.
        description: node.data.description || "",
        platform: definition.platform,
      };
    }

    // Enrich x402 node payloads with endpoint metadata
    // This is needed for AI-generated workflows that don't go through onAddNode
    if (node.data.type === "call-x402-endpoint" && x402Endpoints && x402Endpoints.length > 0) {
      const payload = (node.data.payload || {}) as Record<string, unknown>;

      // Skip if already has metadata (was added via AddActionModal)
      if (!payload._endpointPrice && !payload._endpointNetwork) {
        const endpointId = payload.endpointId as string | undefined;

        // Try to find endpoint by ID first
        let endpoint = endpointId ? x402Endpoints.find(ep => ep.id === endpointId) : undefined;

        // If not found by ID, try to match by name (for AI-generated placeholder IDs)
        if (!endpoint && enrichedData.name) {
          const nodeName = (enrichedData.name as string).toLowerCase();
          endpoint = x402Endpoints.find(ep => ep.name.toLowerCase() === nodeName);
        }

        // If found, add metadata to payload and resultSchema
        if (endpoint) {
          enrichedData = {
            ...enrichedData,
            // Add resultSchema for variable picker to show available output fields
            resultSchema: buildX402ResultSchema(endpoint),
            payload: {
              ...payload,
              endpointId: endpoint.id, // Update to real ID if it was a placeholder
              _endpointName: endpoint.name,
              _endpointDescription: endpoint.description || "",
              _endpointUrl: endpoint.baseUrl,
              _endpointPrice: endpoint.price,
              _endpointNetwork: endpoint.network,
              _endpointTokenSymbol: endpoint.tokenSymbol || "USDC",
              _endpointTokenDecimals: endpoint.tokenDecimals || 6,
            },
          };
        }
      }
    }

    return {
      ...node,
      data: enrichedData,
    };
  });
}

/**
 * Load an AI workflow into React Flow canvas with proper transformation and enrichment.
 * This is a reusable function that handles the complete workflow loading process.
 *
 * @param workflow - The AI workflow to load
 * @param triggers - Available trigger definitions
 * @param actions - Available action definitions
 * @param logicActions - Available logic action definitions
 * @param reactFlowInstance - React Flow instance for auto-fit
 * @param callbacks - Callback functions to update state
 * @param placeholderCallbacks - Optional callbacks for placeholder nodes
 * @param x402Endpoints - Optional x402 endpoints for payload enrichment
 */
export function loadAIWorkflowToCanvas(
  workflow: AIWorkflow,
  triggers: ActionTriggerDefinition[],
  actions: ActionTriggerDefinition[],
  logicActions: ActionTriggerDefinition[],
  reactFlowInstance: ReactFlowInstance | null,
  callbacks: {
    setNodes: (nodes: Node[]) => void;
    setEdges: (edges: Edge[]) => void;
    setWorkflowName: (name: string) => void;
    setWorkflowDescription?: (description: string) => void;
    setSelectedNode: (node: Node | null) => void;
    setMaxRuns?: (maxRuns: number) => void;
    setCooldownMs?: (cooldownMs: number) => void;
    setInputSchema?: (schema: WorkflowInput[]) => void;
    onSuccess?: (workflow: AIWorkflow) => void;
  },
  placeholderCallbacks?: {
    onDropAction?: (placeholderId: string, dragData: any) => void;
    onShowActionModal?: (placeholderId: string) => void;
    onShowLogicModal?: (placeholderId: string) => void;
  },
  x402Endpoints?: X402EndpointForEnrichment[],
): void {
  // Combine all definitions for transformation and enrichment
  const allDefinitions = [...triggers, ...actions, ...logicActions];

  // Transform the AI workflow to React Flow format
  const { nodes: newNodes, edges: newEdges } = transformAIWorkflowToReactFlow(
    workflow,
    allDefinitions,
    placeholderCallbacks,
  );

  // Enrich nodes with definition metadata and x402 endpoint data
  const enrichedNodes = enrichNodesWithDefinitions(newNodes, allDefinitions, x402Endpoints);

  // Update state
  callbacks.setNodes(enrichedNodes);
  callbacks.setEdges(newEdges);
  callbacks.setWorkflowName(workflow.name);
  if (callbacks.setWorkflowDescription && workflow.description) {
    callbacks.setWorkflowDescription(workflow.description);
  }
  callbacks.setSelectedNode(null);

  // Set max runs and cooldown if provided (use != null to allow 0 values)
  if (workflow.maxRuns != null && callbacks.setMaxRuns) {
    callbacks.setMaxRuns(workflow.maxRuns);
  }
  if (workflow.cooldownMs != null && callbacks.setCooldownMs) {
    callbacks.setCooldownMs(workflow.cooldownMs);
  }
  if (callbacks.setInputSchema) {
    callbacks.setInputSchema(getEditorInputSchemaFromDefinition(workflow.definition));
  }

  // Auto-fit view after loading
  setTimeout(() => {
    reactFlowInstance?.fitView({
      duration: AI_CHAT_UI.AUTO_FIT_DURATION,
      padding: AI_CHAT_UI.AUTO_FIT_PADDING,
    });
  }, AI_CHAT_UI.AUTO_FIT_DELAY);

  // Show success toast
  toast.success(AI_CHAT_SUCCESS_MESSAGES.WORKFLOW_LOADED(workflow.name));

  // Call success callback if provided
  callbacks.onSuccess?.(workflow);
}

/**
 * Convert React Flow nodes and edges back to API workflow definition format.
 * This is useful for sending the current canvas state to the AI for context.
 */
export function convertCanvasToDefinition(nodes: Node[], edges: Edge[]): AIWorkflowDefinition | null {
  // Filter out placeholder nodes
  const realNodes = nodes.filter(n => n.type !== "placeholder");
  const realEdges = edges.filter(e => !e.target.startsWith("placeholder-"));

  if (realNodes.length === 0) {
    return null;
  }

  // Build edge maps: regular edges vs onComplete (post-loop) edges.
  // For for-each nodes: edgeMap → loopBody, onCompleteEdgeMap → children (post-loop)
  // For other nodes: edgeMap → children
  const edgeMap = new Map<string, string[]>();
  const onCompleteEdgeMap = new Map<string, string[]>();
  for (const edge of realEdges) {
    if ((edge.data as WorkflowEdgeData)?.isOnComplete) {
      const oc = onCompleteEdgeMap.get(edge.source) || [];
      oc.push(edge.target);
      onCompleteEdgeMap.set(edge.source, oc);
    } else {
      const children = edgeMap.get(edge.source) || [];
      children.push(edge.target);
      edgeMap.set(edge.source, children);
    }
  }

  // Build a map of target node ID -> branch (derived from edge sourceHandle)
  // This is needed because branch info is stored on edges, not nodes
  const nodeBranchMap = new Map<string, "then" | "else" | "resumed" | "timed_out">();
  for (const node of realNodes) {
    const nodeType = (node.data as { type?: string })?.type;
    const isIfNode = node.type === "logic" && nodeType === "if";
    const isWaitNode = node.type === "logic" && nodeType === "wait";
    if (isIfNode) {
      const childEdges = realEdges.filter(edge => edge.source === node.id);
      for (const edge of childEdges) {
        const branch = edge.sourceHandle === "source-else" ? "else" : "then";
        nodeBranchMap.set(edge.target, branch);
      }
    }
    if (isWaitNode) {
      const childEdges = realEdges.filter(edge => edge.source === node.id);
      for (const edge of childEdges) {
        if (edge.sourceHandle === "source-timed_out") {
          nodeBranchMap.set(edge.target, "timed_out");
        } else if (edge.sourceHandle === "source-resumed") {
          nodeBranchMap.set(edge.target, "resumed");
        }
      }
    }
  }

  // Convert nodes to definition format
  const definitionNodes: Record<
    string,
    {
      type: string;
      connector: string | null;
      payload: Record<string, unknown>;
      children: string[];
      loopBody?: string[];
      branch?: "then" | "else" | "resumed" | "timed_out";
    }
  > = {};

  for (const node of realNodes) {
    const nodeId = node.id;
    const nodeData = node.data as {
      type?: string;
      name?: string;
      payload?: Record<string, unknown>;
      connector?: string | WorkflowNodeConnector | null;
      branch?: "then" | "else" | "resumed" | "timed_out";
      titleOverride?: string;
      description?: string;
    };

    // Only include fully-resolved connectors (object with id).
    // AI-generated workflows may have connector as a bare type string (e.g. "telegram-bot")
    // which the backend cannot unmarshal into ConnectorReference.
    const rawConnector = nodeData.connector;
    const resolvedConnector =
      rawConnector && typeof rawConnector === "object" && "id" in rawConnector && rawConnector.id ? rawConnector : null;

    const isForEach = nodeData.type === "for-each";
    const regularTargets = edgeMap.get(nodeId) || [];
    const onCompleteTargets = onCompleteEdgeMap.get(nodeId);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const definitionNode: any = {
      type: nodeData.type || "unknown",
      connector: resolvedConnector,
      payload: nodeData.payload || {},
      // For for-each: regular edges → loopBody, onComplete edges → children (post-loop)
      // For others: regular edges → children
      children: isForEach ? onCompleteTargets || [] : regularTargets,
    };

    if (nodeData.titleOverride) {
      definitionNode.titleOverride = nodeData.titleOverride;
    }
    if (nodeData.description) {
      definitionNode.description = nodeData.description;
    }
    if (nodeData.name) {
      definitionNode.name = nodeData.name;
    }

    // For for-each nodes, regular (non-onComplete) edges are the loop body
    if (isForEach && regularTargets.length > 0) {
      definitionNode.loopBody = regularTargets;
    }

    // Include branch if it exists on node data or was derived from edge sourceHandle
    const derivedBranch = nodeBranchMap.get(nodeId);
    if (nodeData.branch) {
      definitionNode.branch = nodeData.branch;
    } else if (derivedBranch) {
      definitionNode.branch = derivedBranch;
    }

    definitionNodes[nodeId] = definitionNode;
  }

  return { nodes: definitionNodes };
}

/**
 * Extract node positions from React Flow canvas for saving to uiMetadata.
 * This captures the current canvas layout so it can be restored when the workflow is loaded.
 *
 * @param nodes - React Flow nodes from the canvas
 * @returns WorkflowUIMetadata containing node positions
 */
export function extractNodePositions(nodes: Node[], nodeIdMapping?: Map<string, string>): WorkflowUIMetadata {
  // Filter out placeholder nodes - we only save positions for real workflow nodes
  const realNodes = nodes.filter(n => n.type !== "placeholder");

  const nodePositions: Record<string, NodePosition> = {};

  for (const node of realNodes) {
    // Use mapped ID if available (to match API node IDs), otherwise use original ID
    const nodeId = nodeIdMapping?.get(node.id) ?? node.id;
    nodePositions[nodeId] = {
      x: node.position.x,
      y: node.position.y,
    };
  }

  return { nodePositions };
}

/**
 * Connector data for auto-selection
 */
export interface ConnectorSource {
  /** Generic connectors (slack, discord, etc.) */
  genericConnectors: Array<{ id: string; type: string; name: string }>;
  /** Turnkey wallets for crypto actions */
  turnkeyWallets: Array<{ id: string; address: string; name: string; isDefault?: boolean }>;
}

/** Connector selection result */
export interface SelectedConnector {
  id: string;
  type: string;
  walletAddress?: string;
}

/**
 * Get a connector for a definition based on its connector requirements.
 * Core logic shared by both batch (applyConnectorsToNodes) and single (manual add) cases.
 *
 * @param definition - Action/trigger definition with connector requirements
 * @param connectorSource - Available connectors and wallets to select from
 * @returns Selected connector or undefined if none available
 */
export function getConnectorForDefinition(
  definition: { connector?: { type: string } } | undefined,
  connectorSource: ConnectorSource,
): SelectedConnector | undefined {
  if (!definition?.connector?.type) return undefined;

  const requiredType = definition.connector.type;
  const { genericConnectors, turnkeyWallets } = connectorSource;

  // Handle turnkey connector (wallet) - prefer default wallet if available
  if (requiredType === "wallet" && turnkeyWallets.length > 0) {
    const wallet = turnkeyWallets.find(w => w.isDefault) || turnkeyWallets[0];
    return { id: wallet.id, type: "wallet", walletAddress: wallet.address };
  }

  // Handle generic connectors (slack, discord, etc.)
  const matchingConnector = genericConnectors.find(c => c.type === requiredType);
  if (matchingConnector) {
    return { id: matchingConnector.id, type: requiredType };
  }

  return undefined;
}

/**
 * Node types that always require specific connector types.
 * Used as a fallback when the definition isn't in the definitions array
 * (e.g., dynamically generated x402 endpoint definitions).
 */
const NODE_TYPE_CONNECTOR_REQUIREMENTS: Record<string, string> = {
  "call-x402-endpoint": "wallet",
};

/**
 * Get the required connector type for a node.
 * First checks the definition, then falls back to known node type requirements.
 *
 * @param nodeType - The node's type (e.g., "send-erc20-token", "call-x402-endpoint")
 * @param definition - The action/trigger definition (may be undefined for dynamic types)
 * @returns The required connector type or undefined if none required
 */
export function getRequiredConnectorType(
  nodeType: string | undefined,
  definition: { connector?: { type: string } } | undefined,
): string | undefined {
  // First check the definition
  if (definition?.connector?.type) {
    return definition.connector.type;
  }

  // Fall back to known node type requirements
  if (nodeType && NODE_TYPE_CONNECTOR_REQUIREMENTS[nodeType]) {
    return NODE_TYPE_CONNECTOR_REQUIREMENTS[nodeType];
  }

  return undefined;
}

/**
 * Auto-select connectors for workflow nodes that require them.
 *
 * This is a pure function that maps nodes with their appropriate connectors.
 * It handles both generic connectors (Slack, Discord) and turnkey wallets.
 *
 * @param nodes - Current React Flow nodes
 * @param definitions - Action/trigger definitions with connector requirements
 * @param connectorSource - Available connectors and wallets to select from
 * @returns Updated nodes with auto-selected connectors
 */
export function applyConnectorsToNodes(
  nodes: Node[],
  definitions: ActionTriggerDefinition[],
  connectorSource: ConnectorSource,
): Node[] {
  return nodes.map(node => {
    // Skip if already has a COMPLETE connector (with id)
    // A partial connector (e.g., just { type: "wallet" } from AI) should still trigger auto-selection
    const existingConnector = node.data?.connector as { id?: string; type?: string } | undefined;
    if (existingConnector?.id) return node;

    // Find definition (may be undefined for dynamic types like x402 endpoints)
    const definition = definitions.find(d => d.type === node.data?.type);

    // Get required connector type (from definition, existing partial connector, or known node type requirements)
    const requiredType =
      existingConnector?.type || getRequiredConnectorType(node.data?.type as string | undefined, definition);

    if (!requiredType) return node;

    // Get connector based on required type
    const connector = getConnectorForDefinition({ connector: { type: requiredType } }, connectorSource);

    if (connector) {
      return {
        ...node,
        data: {
          ...node.data,
          connector,
        },
      };
    }

    return node;
  });
}
